import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  Attachment,
  CollapseSection,
  isImageOr3dModel,
  LabeledValue,
  Modal,
  processAttachment,
  useFormatting,
} from "@msys/ui";
import {
  Add as AddIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  RemoveDone,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  List,
  ListItem,
  Link as MuiLink,
  Stack,
  Tooltip,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { FieldArray, Form, Formik } from "formik";
import { omit, partition } from "lodash-es";
import moment, { Moment } from "moment";
import React from "react";
import * as Yup from "yup";
import { AttachmentInput } from "../../../clients/graphqlTypes.js";
import { AutoSave } from "../../commons/form-fields/AutoSave.js";
import { DateAndTimePickersField } from "../../commons/form-fields/DateAndTimePickersField.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import { GalleryGrid } from "../../commons/images/GalleryGrid.js";
import { useToggleTodoItemDoneMutation } from "../../main-routes/tasks/TasksList.generated.js";
import { AttachmentSnapshotFragment } from "../attachments/Attachments.generated.js";
import { AttachmentUploader } from "../attachments/AttachmentUploader.js";
import { OrganisationMemberSelect } from "../organisations/OrganisationMemberSelect.js";
import { PlanningIcon } from "../planning/PlanningIcon.js";
import { TeamSelectField } from "../teams/TeamSelectField.js";
import { UserIcon } from "../users/UserIcon.js";
import {
  useEditTodoItemModalQuery,
  useModifyTodoItemMutation,
  useSetTodoTeamMutation,
} from "./EditTodoItemModal.generated.js";
import { RelatedToBox } from "./RelatedToBox.js";

interface FormValues {
  title: string;
  description?: string;
  time?: Moment;
  assigneeId: string;
  attachments: (Attachment | AttachmentSnapshotFragment)[];
  linkedProjectId: string | null;
  linkedCrmCompanyId: string | null;
  linkedCrmPersonId: string | null; // deprecated
  teamId: string | null;
}

interface Props {
  id: string;
  disableAssigneeInput?: boolean;
  title?: string;
  handleClose: () => void;
  refetchQueries?: string[];
}

export const EditTodoItemModal: React.FC<Props> = ({
  id,
  disableAssigneeInput = false,
  title,
  handleClose,
  refetchQueries,
}) => {
  const { t } = useTranslate(["Tasks", "Global"]);
  const { getFormattedDateTime } = useFormatting();

  const client = useApolloClient();
  const query = useEditTodoItemModalQuery({
    client,
    variables: { id },
    fetchPolicy: "cache-and-network",
  });
  const [modifyTodoItem] = useModifyTodoItemMutation({
    client,
    refetchQueries,
  });
  const [setTodoTeam] = useSetTodoTeamMutation({
    client,
    refetchQueries,
  });
  const [toggleTodoDone, { loading: toggleTodoDoneLoading }] =
    useToggleTodoItemDoneMutation({
      client,
      refetchQueries,
    });

  async function onSubmit({
    attachments,
    assigneeId,
    linkedProjectId,
    linkedCrmCompanyId,
    linkedCrmPersonId,
    teamId,
    ...values
  }: FormValues) {
    const res = await modifyTodoItem({
      variables: {
        input: {
          id,
          ...values,
          linkedProjectId: linkedProjectId,
          linkedCrmOrganisationId: linkedCrmCompanyId,
          linkedCrmUserId: linkedCrmPersonId,
          assigneeId: assigneeId || undefined,
          attachments: attachments
            .map(a => omit(a, ["id", "__typename", "__type"]))
            .filter(a => a.url) as AttachmentInput[],
        },
      },
    });

    const todoItemId = res.data?.modifyTodoItem.todoItem.id;
    if (!todoItemId)
      throw new Error(
        t("Failed to update task item", {
          ns: "Tasks",
        })
      );

    if (teamId !== (todoItem?.team?.id ?? null)) {
      await setTodoTeam({ variables: { input: { todoId: id, teamId } } });
    }
  }

  const attachmentUploaderRef = React.useRef<HTMLInputElement>(null);
  const onAddAttachment = () => {
    if (attachmentUploaderRef.current) {
      attachmentUploaderRef.current.click();
    }
  };

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        title: Yup.string()
          .label(
            t("Title", {
              ns: "Tasks",
            })
          )
          .required(),
        description: Yup.string().label(
          t("Description", {
            ns: "Tasks",
          })
        ),
        time: Yup.date().label(
          t("Time", {
            ns: "Tasks",
          })
        ),
        assigneeId: Yup.string().label(
          t("Assignee", {
            ns: "Tasks",
          })
        ),
      }),
    [t]
  );

  const todoItem = getDataOrNull((query.data ?? query.previousData)?.todoItem);

  if (!todoItem) return null;

  const linkedCrmOrganisation = todoItem.linkedCrmOrganisation;
  const linkedCrmUser = todoItem.linkedCrmUser;
  const linkedProject = todoItem.linkedProject;

  return (
    <Formik<FormValues>
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={onSubmit}
      initialValues={{
        title: todoItem.title,
        description: todoItem.description,
        assigneeId: todoItem.assignee?.id ?? "",
        time: todoItem.time ? moment(todoItem.time) : undefined,
        attachments: todoItem.attachments,
        linkedProjectId: linkedProject?.id ?? null,
        linkedCrmCompanyId: linkedCrmOrganisation?.id ?? null,
        linkedCrmPersonId: linkedCrmUser?.id ?? null,
        teamId: todoItem.team?.id ?? null,
      }}
    >
      {({ setFieldValue, setValues, values }) => (
        <Modal
          title={title ?? todoItem?.title}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Close", {
                ns: "Global",
              }),
              handleClick: handleClose,
              buttonProps: { variant: "text" },
            },
            {
              label: todoItem?.isDone
                ? t("Reopen task", { ns: "Tasks" })
                : t("Mark as done", { ns: "Tasks" }),
              handleClick: async () => {
                if (todoItem) {
                  await toggleTodoDone({
                    variables: {
                      input: { id: todoItem.id, isDone: !todoItem.isDone },
                    },
                  });
                }
              },
              buttonProps: {
                color: todoItem?.isDone ? "warning" : "secondary",
                startIcon: todoItem?.isDone ? <RemoveDone /> : <CheckIcon />,
                loading: toggleTodoDoneLoading,
              },
            },
          ]}
        >
          <Form>
            <Stack spacing={1} direction="column">
              <RelatedToBox
                linkedProjectId={values.linkedProjectId}
                linkedCrmCompanyId={values.linkedCrmCompanyId}
                linkedCrmPersonId={values.linkedCrmPersonId}
                handleDelete={() =>
                  setValues(values => ({
                    ...values,
                    linkedProjectId: null,
                    linkedCrmCompanyId: null,
                    linkedCrmPersonId: null,
                  }))
                }
                handleChange={(crmCompanyId, projectId) => {
                  setValues(values => ({
                    ...values,
                    linkedProjectId: projectId,
                    linkedCrmCompanyId: crmCompanyId,
                    linkedCrmUserId: null,
                  }));
                }}
              />
              <TextField
                label={t("Title", {
                  ns: "Tasks",
                })}
                name="title"
                required
                disabled={false}
              />
              <TextField
                label={t("Description", {
                  ns: "Tasks",
                })}
                name="description"
                multiline
                rows={4}
                disabled={false}
              />
              <DateAndTimePickersField
                name="time"
                dateLabel={t("Date", {
                  ns: "Tasks",
                })}
                timeLabel={t("Time", {
                  ns: "Tasks",
                })}
                disabled={false}
              />
              <OrganisationMemberSelect
                inputLabel={t("Assignee", {
                  ns: "Tasks",
                })}
                sorting={[{ column: "firstname", direction: "asc" }]}
                filters={{ statuses: ["ACTIVATED"] }}
                disabled={disableAssigneeInput}
                value={values.assigneeId}
                onChange={value => {
                  setFieldValue("assigneeId", value);
                }}
              />

              <TeamSelectField
                value={values.teamId}
                currentTeam={todoItem.team}
                onChange={teamId => setFieldValue("teamId", teamId)}
              />

              <CollapseSection
                title={t("Files", {
                  ns: "Tasks",
                })}
                itemCount={values.attachments.length}
                ActionButtons={
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => {
                      onAddAttachment();
                    }}
                  >
                    <AddIcon />
                    <AttachmentUploader
                      innerRef={attachmentUploaderRef}
                      accept={"*"}
                      multiple={true}
                      onComplete={attachments => {
                        setFieldValue("attachments", [
                          ...values.attachments,
                          ...attachments,
                        ]);
                      }}
                    />
                  </IconButton>
                }
              >
                <FieldArray
                  name="attachments"
                  render={arrayHelpers => {
                    const typedAttachments =
                      values.attachments.map(processAttachment);
                    const [images, files] = partition(
                      typedAttachments,
                      isImageOr3dModel
                    );
                    return (
                      <>
                        <Box>
                          <GalleryGrid
                            images={images}
                            handleClick={null}
                            handleDelete={image => {
                              arrayHelpers.remove(
                                typedAttachments.indexOf(image)
                              );
                            }}
                            columns={4}
                            showDeleteButton={true}
                          />
                        </Box>
                        {files.length > 0 && (
                          <List disablePadding>
                            {files.map((file, index) => (
                              <Stack
                                spacing={1}
                                direction="row"
                                key={index}
                                sx={{
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <ListItem>
                                  <MuiLink href={file.url} target="_blank">
                                    <Stack spacing={1} direction="row">
                                      <DescriptionIcon fontSize="small" />
                                      {file.title}
                                    </Stack>
                                  </MuiLink>
                                </ListItem>
                                <Tooltip
                                  title={t("Delete File", {
                                    ns: "Tasks",
                                  })}
                                >
                                  <IconButton
                                    aria-label={t("Delete File", {
                                      ns: "Tasks",
                                    })}
                                    color="primary"
                                    onClick={() => {
                                      arrayHelpers.remove(
                                        typedAttachments.indexOf(file)
                                      );
                                    }}
                                    size="small"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            ))}
                          </List>
                        )}
                      </>
                    );
                  }}
                />
              </CollapseSection>

              <CollapseSection
                title={t("More details", {
                  ns: "Tasks",
                })}
                isInitiallyExpanded={false}
              >
                <Stack spacing={1} direction="column">
                  <Stack spacing={1} direction="row">
                    <Box
                      sx={{
                        width: "50%",
                      }}
                    >
                      <DetailItem
                        Icon={<UserIcon />}
                        label={t("Created by", {
                          ns: "Tasks",
                        })}
                        value={todoItem?.createdBy.fullname}
                      />
                    </Box>
                    <Box
                      sx={{
                        width: "50%",
                      }}
                    >
                      <DetailItem
                        Icon={<PlanningIcon />}
                        label={t("Created at", {
                          ns: "Tasks",
                        })}
                        value={
                          todoItem?.createdAt &&
                          getFormattedDateTime(todoItem?.createdAt)
                        }
                      />
                    </Box>
                  </Stack>
                  {todoItem?.completedAt && (
                    <Stack spacing={1} direction="row">
                      <Box
                        sx={{
                          width: "50%",
                        }}
                      >
                        <DetailItem
                          Icon={<UserIcon />}
                          label={t("Completed by", {
                            ns: "Tasks",
                          })}
                          value={todoItem?.completedBy?.fullname}
                        />
                      </Box>
                      <Box
                        sx={{
                          width: "50%",
                        }}
                      >
                        <DetailItem
                          Icon={<PlanningIcon />}
                          label={t("Completed at", {
                            ns: "Tasks",
                          })}
                          value={
                            todoItem?.completedAt &&
                            getFormattedDateTime(todoItem?.completedAt)
                          }
                        />
                      </Box>
                    </Stack>
                  )}
                </Stack>
              </CollapseSection>
              <AutoSave />
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

function DetailItem({
  Icon,
  label,
  value = "",
}: {
  Icon: JSX.Element;
  label: string;
  value: string | undefined;
}) {
  return (
    <Stack
      spacing={1}
      direction="row"
      useFlexGap
      sx={{
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          marginY: 0.5,
        }}
      >
        {Icon}
      </Box>
      <Box
        sx={{
          alignSelf: "flex-start",
        }}
      >
        <LabeledValue label={label}>{value}</LabeledValue>
      </Box>
    </Stack>
  );
}
